import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as AgenticV2API from "@/services/api/agentic-v2";
import { useCompanyId } from "@/context/CompanyContext";
import { useWindowLive } from "@/components/ui/os-layout/WindowActiveContext";
import { clientsKeys } from "./keys";

export interface UseClientInsightOptions {
  faClientId: string;
  enabled?: boolean;
}

/** Latest stored AI healthcheck insight for one fa_client. */
export function useClientInsight({
  faClientId,
  enabled: enabledOption = true,
}: UseClientInsightOptions) {
  const isWindowLive = useWindowLive();
  const companyId = useCompanyId();
  const queryEnabled = Boolean(faClientId) && enabledOption && isWindowLive;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: clientsKeys.insight(faClientId, companyId ?? undefined),
    enabled: queryEnabled,
    refetchOnWindowFocus: queryEnabled,
    queryFn: () => AgenticV2API.getClientInsight(faClientId),
  });

  const refreshMutation = useMutation({
    mutationFn: () => AgenticV2API.refreshClientInsight(faClientId),
    onSuccess: (fresh) => {
      queryClient.setQueriesData(
        { queryKey: [...clientsKeys.all, "insight", faClientId] },
        () => fresh,
      );
    },
  });

  return {
    insight: query.data ?? null,
    loading: query.isLoading,
    refreshing: refreshMutation.isPending,
    error: query.error ?? refreshMutation.error,
    refreshInsight: () => refreshMutation.mutateAsync(),
  };
}
